import { listEstablishmentsQueryOptions } from "@packages/api-client/hooks/establishmentController";
import { FeasibilityStudyType } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import {
	ArrowRight,
	Check,
	Compass,
	Loader2,
	type LucideIcon,
	Sprout,
} from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateStudy } from "@/api/feasibility/studies";
import { CowIcon } from "@/components/icons/CowIcon";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/utils/utils";

const STUDY_TYPES = [FeasibilityStudyType.COW_CALF];

// Each modelable operation gets its own emblem; map scales as types are added.
const STUDY_TYPE_ICONS: Record<FeasibilityStudyType, LucideIcon> = {
	[FeasibilityStudyType.COW_CALF]: CowIcon,
};

// The six wizard sections the user walks through after creating the study.
// Keys mirror `feasibility.wizard.steps.*` so labels stay in one place.
const WIZARD_STEP_KEYS = [
	"farmForage",
	"herdDefinition",
	"reproductionSurvival",
	"nutritionIntake",
	"commercialFinancial",
	"review",
] as const;

// Staggered entrance shared by every revealed block; delay set inline per index.
const reveal = "animate-in fade-in slide-in-from-bottom-2 fill-mode-both";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/feasibility/new",
)({
	loader: async () => {},
	component: NewFeasibilityStudyPage,
});

function NewFeasibilityStudyPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/feasibility/new",
	});
	const nameId = useId();
	const descriptionId = useId();
	const establishmentId = useId();
	const startDateId = useId();

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [type, setType] = useState<FeasibilityStudyType>(
		FeasibilityStudyType.COW_CALF,
	);
	const [selectedEstablishmentId, setSelectedEstablishmentId] = useState("");
	const [startDate, setStartDate] = useState(
		new Date().toISOString().split("T")[0],
	);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data: establishmentsResponse } = useQuery(
		listEstablishmentsQueryOptions(orgId, { limit: 100 }),
	);
	const establishments = establishmentsResponse?.data || [];

	const createStudy = useCreateStudy();

	const handleCreate = async () => {
		if (!name.trim() || !selectedEstablishmentId || !startDate || isSubmitting)
			return;

		setIsSubmitting(true);
		try {
			const study = await createStudy.mutateAsync({
				organizationId: orgId,
				data: {
					name: name.trim(),
					description: description.trim() || undefined,
					type,
					establishmentId: selectedEstablishmentId,
					startDate,
				},
			});

			navigate({
				to: "/app/org/$orgId/feasibility/$studyId",
				params: { orgId, studyId: study.id },
			});
		} catch (error) {
			console.error("Failed to create study:", error);
			setIsSubmitting(false); // Reset on error so user can retry
		}
	};

	const handleCancel = () => {
		navigate({ to: "/app/org/$orgId/feasibility", params: { orgId } });
	};

	const isFormValid =
		name.trim() && selectedEstablishmentId && startDate && !isSubmitting;

	return (
		<div className="h-full overflow-y-auto">
			<div className="mx-auto flex w-full max-w-6xl flex-col gap-6 pb-10">
				<PageHeader
					title={t("feasibility.list.newStudy")}
					description={t("feasibility.new.lede")}
					showBackButton
					backButtonText={t("common.back")}
				/>

				<div className="grid gap-6 lg:grid-cols-5">
					{/* Intake form */}
					<Card
						className={cn(
							reveal,
							"overflow-hidden shadow-md lg:col-span-3 duration-500",
						)}
					>
						{/* Emblem header — sets the agronomic tone without a heavy banner */}
						<div className="flex items-start gap-4 border-b border-base-300 bg-gradient-to-br from-primary/8 to-secondary/5 p-6">
							<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20">
								<Sprout className="h-6 w-6" />
							</div>
							<div className="min-w-0">
								<h2 className="font-display text-xl font-semibold leading-tight text-base-content">
									{t("feasibility.new.formTitle")}
								</h2>
								<p className="mt-0.5 text-sm text-base-content/70">
									{t("feasibility.new.formSubtitle")}
								</p>
							</div>
						</div>

						<CardContent className="space-y-6 p-6">
							<div
								className={cn(reveal, "space-y-2 duration-500")}
								style={{ animationDelay: "60ms" }}
							>
								<Label htmlFor={nameId}>
									{t("common.name")}
									<span className="ml-1 text-error">*</span>
								</Label>
								<Input
									id={nameId}
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder={t("common.namePlaceholder")}
								/>
							</div>

							{/* Where + when, paired on one row to keep the form compact */}
							<div
								className={cn(reveal, "grid gap-4 duration-500 sm:grid-cols-2")}
								style={{ animationDelay: "120ms" }}
							>
								<div className="space-y-2">
									<Label htmlFor={establishmentId}>
										{t("common.establishment")}
										<span className="ml-1 text-error">*</span>
									</Label>
									<Select
										value={selectedEstablishmentId}
										onValueChange={setSelectedEstablishmentId}
									>
										<SelectTrigger id={establishmentId} className="w-full">
											<SelectValue
												placeholder={t("common.selectEstablishment")}
											/>
										</SelectTrigger>
										<SelectContent>
											{establishments?.map((est) => (
												<SelectItem key={est.id} value={est.id}>
													{est.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<Label htmlFor={startDateId}>
										{t("common.startDate")}
										<span className="ml-1 text-error">*</span>
									</Label>
									<Input
										id={startDateId}
										type="date"
										value={startDate}
										onChange={(e) => setStartDate(e.target.value)}
									/>
								</div>
							</div>

							<div
								className={cn(reveal, "space-y-2 duration-500")}
								style={{ animationDelay: "180ms" }}
							>
								<Label htmlFor={descriptionId}>{t("common.description")}</Label>
								<Textarea
									id={descriptionId}
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder={t("common.descriptionPlaceholder")}
									className="min-h-20"
								/>
							</div>

							{/* Operation type as tactile, scannable tiles */}
							<fieldset
								className={cn(reveal, "space-y-3 duration-500")}
								style={{ animationDelay: "240ms" }}
							>
								<legend className="space-y-0.5">
									<span className="text-sm font-medium text-base-content">
										{t("feasibility.new.typeSection")}
									</span>
									<p className="text-xs text-base-content/60">
										{t("feasibility.new.typeSectionHint")}
									</p>
								</legend>
								<div className="grid gap-3 sm:grid-cols-2">
									{STUDY_TYPES.map((studyType) => {
										const Icon = STUDY_TYPE_ICONS[studyType];
										const selected = type === studyType;
										return (
											<button
												key={studyType}
												type="button"
												aria-pressed={selected}
												onClick={() => setType(studyType)}
												className={cn(
													"group relative flex items-start gap-3 rounded-lg border p-4 text-left transition-all duration-200",
													selected
														? "border-primary bg-primary/5 ring-1 ring-primary shadow-sm"
														: "border-base-300 hover:border-primary/40 hover:bg-base-200/40",
												)}
											>
												<span
													className={cn(
														"flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors",
														selected
															? "bg-primary text-primary-content"
															: "bg-base-200 text-base-content/70 group-hover:text-primary",
													)}
												>
													<Icon className="h-5 w-5" />
												</span>
												<span className="min-w-0 flex-1">
													<span className="block text-sm font-semibold text-base-content">
														{t(`feasibility.types.${studyType}`)}
													</span>
													<span className="mt-0.5 block text-xs leading-snug text-base-content/60">
														{t(`feasibility.new.descriptions.${studyType}`)}
													</span>
												</span>
												<span
													className={cn(
														"flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-all",
														selected
															? "border-primary bg-primary text-primary-content"
															: "border-base-300 text-transparent",
													)}
												>
													<Check className="h-3 w-3" strokeWidth={3} />
												</span>
											</button>
										);
									})}
								</div>
							</fieldset>

							<div
								className={cn(
									reveal,
									"flex flex-col-reverse gap-3 border-t border-base-300 pt-5 duration-500 sm:flex-row sm:justify-end",
								)}
								style={{ animationDelay: "300ms" }}
							>
								<Button
									variant="outline"
									onClick={handleCancel}
									className="sm:w-auto"
								>
									{t("common.cancel")}
								</Button>
								<Button
									onClick={handleCreate}
									disabled={!isFormValid}
									className="gap-2 sm:w-auto"
								>
									{isSubmitting ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" />
											{t("common.creating")}
										</>
									) : (
										<>
											{t("common.create")}
											<ArrowRight className="h-4 w-4" />
										</>
									)}
								</Button>
							</div>
						</CardContent>
					</Card>

					{/* Itinerary — frames the journey the study kicks off */}
					<aside
						className={cn(reveal, "duration-500 lg:col-span-2")}
						style={{ animationDelay: "120ms" }}
					>
						<Card className="overflow-hidden bg-gradient-to-br from-base-100 to-base-200/40 lg:sticky lg:top-4">
							<CardContent className="space-y-5 p-6">
								<div className="flex items-center gap-2.5">
									<span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/20 text-accent">
										<Compass className="h-5 w-5" />
									</span>
									<div>
										<h3 className="font-display text-base font-semibold text-base-content">
											{t("feasibility.new.whatsNextTitle")}
										</h3>
									</div>
								</div>

								<p className="text-sm text-base-content/70">
									{t("feasibility.new.whatsNextSubtitle")}
								</p>

								{/* Numbered timeline of the wizard sections */}
								<ol className="relative space-y-1">
									{WIZARD_STEP_KEYS.map((stepKey, index) => (
										<li
											key={stepKey}
											className="relative flex gap-3 pb-4 last:pb-0"
										>
											{index !== WIZARD_STEP_KEYS.length - 1 && (
												<span
													aria-hidden
													className="absolute left-[15px] top-8 h-[calc(100%-1.5rem)] w-px bg-base-300"
												/>
											)}
											<span className="z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-base-300 bg-base-100 text-xs font-semibold text-base-content/70">
												{index + 1}
											</span>
											<span className="pt-1.5 text-sm font-medium text-base-content">
												{t(`feasibility.wizard.steps.${stepKey}`)}
											</span>
										</li>
									))}
								</ol>

								<p className="rounded-lg border border-base-300 bg-base-100/60 p-3 text-xs leading-relaxed text-base-content/70">
									{t("feasibility.new.whatsNextNote")}
								</p>
							</CardContent>
						</Card>
					</aside>
				</div>
			</div>
		</div>
	);
}
